import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { pick, path as ramdaPath } from 'ramda';
import { useNavigate } from 'js/hooks/navigate';
import { AUTH_REDUCER } from 'js/store/constants';
import { addParamsToUrl } from 'js/utils/url.util';
import { Redirect } from 'js/components/common/Redirect';
import { useLoginPopup } from 'js/components/popups/LoginPopup';
import { ErrorMessage } from 'js/components/common/ErrorMessage';
import { useAppSelector, useUserSelector } from 'js/store/hooks';

import * as hooks from './hooks';

import style from './withCommonPage.scss';

export const withCommonPage = (WrappedComponent, isPageInPopup) => (props) => {
    const { path, isPopupBackgroundPage } = props;
    const navigate = useNavigate();
    const showLoginPopup = useLoginPopup();
    const { http404Configuration, rootPage, loginPage, redirects } = useAppSelector(pick([
        'http404Configuration', 'rootPage', 'loginPage', 'redirects'
    ]));
    const { isLoggedIn, role } = useUserSelector(pick(['isLoggedIn', 'role']));
    const [{ isLoading, pageError, page }, fetchPageInfo] = hooks.usePageInfoFetch({ path, isPageInPopup });
    const isLogoutInProcess = useSelector(ramdaPath([AUTH_REDUCER, 'isLogoutInProcess']));
    const { error, is404Error, is403Error } = pageError || {};

    hooks.useForwardRootPage({ page });
    hooks.usePageAsPopupBackground({ isPageInPopup, path, page, pageError });

    useEffect(() => {
        if (!is403Error || role === 'admin' || isPopupBackgroundPage || pageError.path !== path) {
            return;
        }

        const { permissions } = error;
        const { user, guest } = permissions;

        if (user && !guest && !isLoggedIn && !loginPage && !isLogoutInProcess) {
            showLoginPopup({ onLogin: () => navigate(path) });
        }
    }, [
        isLoggedIn, role, is403Error, error, loginPage, path, pageError,
        navigate, showLoginPopup, isPopupBackgroundPage, isLogoutInProcess
    ]);

    if (is404Error) {
        const { http404RedirectionType, url, page: page404 } = http404Configuration;

        switch (http404RedirectionType) {
            case 'URL':
                return <Redirect to={url} />;

            case 'PAGE':
                return <Redirect to={page404} />;

            default:
                if (path === rootPage) {
                    const errorMessage = typeof error === 'string' || error instanceof window.Error ? error.toString() : '';

                    return !isPageInPopup ? <ErrorMessage className={style.error} message={errorMessage}/> : null;
                }

                return <Redirect to={rootPage} />;
        }
    }

    if (is403Error) {
        if (isPopupBackgroundPage || pageError.path !== path) {
            return null;
        }

        const isRootPageRedirect = Object.entries(redirects)
            .find(([fromPage, toPage]) => toPage === path && fromPage === rootPage);

        if (isRootPageRedirect) {
            return null;
        }

        const { permissions } = error;
        const { user, guest } = permissions;

        if (!user && !guest) {
            return <Redirect to={rootPage} />;
        }

        if (user && !guest && !isLoggedIn) {
            const redirectUrl = loginPage ? addParamsToUrl({ afterLoginUrl: path }, loginPage) : rootPage;

            return <Redirect to={redirectUrl} />;
        }

        if (!user && guest && isLoggedIn) {
            return <Redirect to={rootPage} />;
        }

        // cannot redirect here, because user state changes async
        return null;
    }

    if (error && !isPageInPopup) {
        const errorMessage = typeof error === 'string' || error instanceof window.Error ? error.toString() : '';

        return <ErrorMessage className={style.error} message={errorMessage} />;
    }

    return (
        <WrappedComponent
            {...props}
            page={page}
            error={error}
            isLoading={isLoading}
            fetchPageInfo={fetchPageInfo}
        />
    );
};
